import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, RotateCcw, TrendingUp, AlertOctagon, Clock, RefreshCw } from 'lucide-react';
import InteractiveBackground from '@/components/ui/InteractiveBackground';
import { ReadingHeader } from '@/components/gol/ReadingHeader';
import { ReadingNext } from '@/components/gol/ReadingNext';

const Philosophy = () => {
  return (
    <div data-testid="philosophy-page" className="reading-page min-h-screen bg-abyss-950 text-sand-50 font-sans selection:bg-teal-400/30 overflow-x-hidden relative">
      <InteractiveBackground />

      <div className="relative z-10 pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto space-y-32">
            
            {/* Header */}
            <ReadingHeader id="philosophy" category="The wider argument" title="Make the living system visible to the decision." intro="Ecological value and financial value move through different institutions, time horizons and measures. Building a useful bridge means understanding that difference first." />

            {/* Section 1: The Banking Paradox */}
            <motion.section 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="prose prose-invert prose-lg text-sand-50/80 font-light leading-relaxed"
            >
                <p>
                    Consider two futures for the same forest. In one, the timber gets harvested. In the other, the watershed protection, habitat and carbon storage keep running.
                </p>
                <p>
                    Timber has familiar units, buyers and revenue schedules. A lender can examine a proposed harvest against established accounting and lending practices.
                </p>
                <p>
                    The standing forest provides a different kind of value. Its benefits reach people beyond the landowner, emerge over long periods and depend on relationships that are hard to observe directly.
                </p>
                <p className="text-sand-50 font-medium text-xl border-l-2 border-teal-500 pl-4 my-8">
                    The decision changes when the relationships supporting value become visible.
                </p>
                <p>
                    This is a thought experiment about what an assessment can capture. Whether either project merits financing depends on its own evidence, rights, economics and institutional review.
                </p>
                <p className="text-2xl font-serif italic mt-8 text-teal-400">
                    What would a fuller account require?
                </p>
            </motion.section>

            {/* Section 2: Three Interlocking Failures */}
            <motion.section 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="space-y-16"
            >
                <div className="space-y-6">
                    <h2 className="text-3xl font-serif text-sand-50">Three gaps in the picture</h2>
                    <p className="text-lg text-sand-50/60 font-light leading-relaxed max-w-2xl">
                        Accounting, feedback and incentives each shape which consequences enter a decision. Examining all three helps explain where ecological dependencies can disappear from view.
                    </p>
                </div>

                {/* Visual Diagram of Failures */}
                <div className="grid gap-8 relative">
                    {/* Connecting Line (Desktop) */}
                    <div className="hidden md:block absolute left-[2rem] top-8 bottom-8 w-px bg-gradient-to-b from-teal-500/0 via-teal-500/20 to-teal-500/0" />

                    <FailureCard 
                        number="01"
                        title="What the accounts capture"
                        subtitle="Stocks, flows and dependencies"
                        icon={TrendingUp}
                        content="A transaction records the value exchanged. But changes to habitat, water regulation and other ecosystem functions may sit in different accounts entirely, or go unmeasured. A dependency model brings those consequences into the same frame."
                    />

                    <FailureCard 
                        number="02"
                        title="When the signal arrives"
                        subtitle="Different clocks"
                        icon={AlertOctagon}
                        content="Institutional reporting and ecological change unfold over different periods. Some effects emerge in months; others accumulate across decades. The monitoring has to match both the process being investigated and the time horizon of the decision."
                    />

                    <FailureCard 
                        number="03"
                        title="Who carries the consequence"
                        subtitle="Benefits, costs and responsibility"
                        icon={Clock}
                        content="The people who benefit immediately are often different from those who bear the ecological cost later. Tracing beneficiaries, affected communities and governance conditions makes that distribution visible, together with the evidence needed to assess it."
                    />
                </div>
            </motion.section>

            {/* Section 3: The Paradigm Shift */}
            <motion.section 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="space-y-12 py-12 border-t border-abyss-800"
            >
                <div className="space-y-6">
                    <h2 className="text-3xl font-serif text-sand-50">A more complete basis for review</h2>
                    <p className="text-lg text-sand-50/60 font-light leading-relaxed">
                        The aim is to make ecological dependencies legible enough to examine next to existing institutional requirements. What follows shows a direction of travel. Outcomes still depend on evidence and accountable decisions.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-px bg-abyss-800 border border-abyss-800 rounded-lg overflow-hidden">
                    <div className="p-8 bg-abyss-950 space-y-8">
                        <h3 className="text-xs font-mono uppercase tracking-widest text-risk-bright">Where the picture narrows</h3>
                        <ul className="space-y-6">
                            <ParadigmItem 
                                from="Transactions alone"
                                desc="The immediate exchange dominates."
                            />
                            <ParadigmItem 
                                from="Disconnected evidence"
                                desc="Sources and assumptions are hard to trace."
                            />
                            <ParadigmItem 
                                from="Distant consequences"
                                desc="Effects fall beyond the review boundary."
                            />
                        </ul>
                    </div>
                    
                    <div className="p-8 bg-abyss-900/30 space-y-8 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <RefreshCw size={100} className="text-teal-500" />
                        </div>
                        <h3 className="text-xs font-mono uppercase tracking-widest text-teal-400">What the review can include</h3>
                        <ul className="space-y-6">
                            <ParadigmItem 
                                to="Living dependencies"
                                desc="Functions and relationships enter the frame."
                                isTarget
                            />
                            <ParadigmItem 
                                to="Traceable reasoning"
                                desc="Sources, assumptions and limits stay visible."
                                isTarget
                            />
                            <ParadigmItem 
                                to="Accountable choices"
                                desc="Consequences and responsibilities are examined."
                                isTarget
                            />
                        </ul>
                    </div>
                </div>
            </motion.section>

            {/* Footer / CTA */}
            <ReadingNext id="philosophy" to="/methodology" title="Bring the argument into practice." copy="A bounded method for tracing dependencies and preparing evidence for review." />

        </div>
      </div>
    </div>
  );
};

const FailureCard = ({ number, title, subtitle, icon: Icon, content }) => (
    <div data-testid={`philosophy-gap-${number}`} className="relative pl-5 md:pl-16 group">
        {/* Node/Gear Indicator */}
        <div className="absolute left-0 md:left-4 top-0 w-8 h-8 rounded-full bg-abyss-900 border border-abyss-700 flex items-center justify-center text-xs font-mono text-teal-500/50 group-hover:border-teal-500 group-hover:text-teal-400 transition-colors z-10">
            {number}
        </div>
        
        <div className="p-8 bg-abyss-900/20 border border-abyss-800 rounded-lg hover:border-teal-500/20 transition-colors">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h3 className="text-xl font-serif text-sand-50 mb-1">{title}</h3>
                    <div className="text-xs font-mono uppercase tracking-widest text-teal-500/60">{subtitle}</div>
                </div>
                <Icon className="text-abyss-700 group-hover:text-teal-500/20 transition-colors" size={32} />
            </div>
            <p className="text-sand-50/70 font-light leading-relaxed text-sm">
                {content}
            </p>
        </div>
    </div>
);

const ParadigmItem = ({ from, to, desc, isTarget }) => (
    <div className="space-y-1">
        <div className={`font-serif text-xl ${isTarget ? 'text-teal-300' : 'text-sand-50/60'}`}>
            {from || to}
        </div>
        <div className="text-xs font-mono text-sand-50/40 uppercase tracking-wide">
            {desc}
        </div>
    </div>
);

export default Philosophy;
